import { type ReactNode, useState } from 'react'
import styled from 'styled-components'
import { Body, Headline } from '@shipt/design-system-typography'
import { type SplitInfoAccordionContainerData } from '@/features/cms/components/CMS/types'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { spacing } from '@/features/shared/theme/tokens'
import { screenSizes } from '@shipt/design-system-themes'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `split_info_accordion_container` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/split_info_accordion_container
 * - **Production** - https://cms.shipt.com/content-type/pool/split_info_accordion_container
 */
export const SplitInfoAccordion = ({
  content_type_id,
  id,
  data: { heading, image, image_placement, references, subheading },
}: SplitInfoAccordionContainerData) => {
  try {
    return (
      <SplitInfoContainer {...getCMSWrapperProps({ content_type_id, id })}>
        <Pane imagePlacement={image_placement === 'left' ? 'right' : 'left'}>
          <ContentContainer>
            <Title as="h2" size="xl">
              {heading}
            </Title>
            <Subheading>{subheading}</Subheading>
            {references.map(({ data: { heading: accordionHeading, text } }, index) => (
              <HorizontalAccordion title={accordionHeading} index={index} key={accordionHeading}>
                <AccordionItem
                  variant="secondary"
                  as="div"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(text) }}
                />
              </HorizontalAccordion>
            ))}
          </ContentContainer>
        </Pane>
        <Pane imagePlacement={image_placement}>
          <PaneImage
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            style={{ objectFit: 'contain', height: 'auto' }}
          />
        </Pane>
      </SplitInfoContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

type HorizontalAccordionProps = {
  title: string
  children: ReactNode
  index: number
}

const HorizontalAccordion = ({ title, children, index }: HorizontalAccordionProps) => {
  const { trackCMSElementViewed } = useCMSTrackingEvents()
  const [isOpen, setIsOpen] = useState(index === 0 ? true : false)
  const handleClick = () => {
    setIsOpen(!isOpen)
    if (!isOpen) trackCMSElementViewed({ content: title })
  }

  return (
    <AccordionWrapper>
      <AccordionButton
        as="button"
        variant="secondary"
        strong
        onClick={handleClick}
        aria-label={`Toggle accordion - ${title}`}
        index={index}
      >
        <AccordionTitle>{title}</AccordionTitle>
        <Arrow isOpen={isOpen} />
      </AccordionButton>
      <Content hidden={!isOpen}>{children}</Content>
    </AccordionWrapper>
  )
}

const SplitInfoContainer = styled.section`
  margin: ${spacing(0, 'auto')};
  max-width: 67.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${spacing(0, 'lg')};

  @media ${screenSizes.tablet} {
    flex-direction: row;
    gap: ${spacing('lg')};

    /* Sibling SplitInfoAccordions should be stacked without extra padding */
    & + & {
      padding-top: ${spacing(0)};
    }
  }

  @media ${screenSizes.smDesktop} {
    padding-right: ${spacing(40)};
    padding-left: ${spacing(40)};
  }
`

const Pane = styled.div<{ imagePlacement: string }>`
  flex: 1;
  max-width: 21.4375rem;
  margin: ${spacing(0, 'auto', 30)};
  width: 100%;
  height: 100%;

  &:last-of-type {
    margin-bottom: ${spacing(0)};
  }

  @media ${screenSizes.tablet} {
    order: ${({ imagePlacement }) => (imagePlacement === 'left' ? 1 : 2)};
    max-width: 33rem;
  }
`

const PaneImage = styled(Image)`
  @media ${screenSizes.max_mobile} {
    width: 100%;
    height: auto;
  }
`

const AccordionWrapper = styled.div`
  &:last-child {
    border-bottom: 1px solid ${theme.gray300};
  }
`

const AccordionButton = styled(Body)<{ index: number }>`
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  width: 100%;
  -webkit-tap-highlight-color: transparent;
  padding: ${spacing('xl', 0)};
  border-top: ${({ index }) => (index > 0 ? `1px solid ${theme.gray300}` : 'none')};
`
const AccordionTitle = styled.div`
  width: 100%;
`

const Content = styled.div`
  white-space: pre-line;
`

const Arrow = styled.span<{ isOpen: boolean }>`
  border-width: 2.25px 2.25px 0 0;
  display: block;
  transition: all 0.3s ease-in-out;
  border-style: solid;
  height: 0.4375rem;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(135deg)' : 'rotate(45deg)')};
  width: 0.4375rem;
  color: ${theme.gray600};
`

const ContentContainer = styled.div`
  max-width: 31.25rem;
  margin: ${spacing(0, 'auto')};
`

const Title = styled(Headline)`
  margin: ${spacing(0, 0, 'xl')};
`

const Subheading = styled(Body)`
  border-bottom: 1px solid ${theme.gray300};
  padding-bottom: ${spacing('xl')};
`

const AccordionItem = styled(Body)`
  margin-bottom: ${spacing('xxl')};

  p:first-child {
    margin-top: ${spacing(0)};
  }

  a {
    color: ${theme.purple500};

    &:hover,
    &:focus {
      color: ${theme.purple700};
    }
  }
`
